import { useCallback, useEffect, useState } from 'react';
import { Confirm, useNotify, usePermissions, useTranslate } from 'react-admin';
import { Alert, Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { PermissionData } from '../../entities/permission.entity';
import { hasPermission } from '../../utils/has-permission';
import { SESSION_PAGE_SIZE, SessionTable } from '../../components/Session/SessionTable';
import * as sessionService from '../../services/sessionService';

interface UserSessionsPopupProps {
  id: string;
  username?: string;
  onClose: () => void;
}

interface PendingAction {
  content: string;
  run: () => Promise<void>;
}

// XBIT-4828 D9: another user's sessions, rendered inside the Users list's popup Dialog. The server applies the
// user-management privilege rule; a refusal replaces the table with its message.
const UserSessionsPopup = ({ id, username, onClose }: UserSessionsPopupProps) => {
  const t = useTranslate();
  const notify = useNotify();
  const { permissions } = usePermissions();
  // End / End all are part of users:revoke-sessions; the list itself is behind users:read-sessions (the row action).
  const canEnd = hasPermission((permissions as PermissionData)?.permissions, 'users', 'revoke-sessions');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<sessionService.SessionView[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [acting, setActing] = useState(false);

  const messageOf = useCallback(
    (error: any) => error?.response?.data?.message || t('module.session.failed'),
    [t]
  );

  const load = useCallback(
    async (target: number) => {
      setLoading(true);
      try {
        const res = await sessionService.listUserSessions(id, target, SESSION_PAGE_SIZE);
        setRows(res.data.data);
        setTotal(res.data.total);
        setLoadError(null);
      } catch (error: any) {
        setLoadError(messageOf(error));
      } finally {
        setLoading(false);
      }
    },
    [id, messageOf]
  );

  useEffect(() => {
    load(page);
  }, [page, load]);

  const confirmed = async () => {
    if (!pending) return;
    setActing(true);
    try {
      await pending.run();
      await load(page);
    } catch (error: any) {
      notify(messageOf(error), { type: 'error' });
    } finally {
      setActing(false);
      setPending(null);
    }
  };

  const askEnd = (sid: string) =>
    setPending({
      content: t('module.session.confirmEnd'),
      run: async () => {
        await sessionService.revokeUserSession(id, sid);
        notify(t('module.session.ended'), { type: 'success' });
      },
    });

  const askEndAll = () =>
    setPending({
      content: t('module.session.confirmEndAll', { username: username ?? id }),
      run: async () => {
        const res = await sessionService.revokeAllUserSessions(id);
        notify(t('module.session.endedMany', { count: res.data.revokedSessionIds.length }), {
          type: 'success',
        });
      },
    });

  return (
    <>
      <DialogTitle>{t('module.session.userSessions', { username: username ?? id })}</DialogTitle>
      <DialogContent>
        {loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <SessionTable
            rows={rows}
            total={total}
            page={page}
            onPageChange={setPage}
            loading={loading}
            canEnd={canEnd}
            onEnd={askEnd}
          />
        )}
      </DialogContent>
      <DialogActions>
        {!loadError && canEnd && (
          <Button color="error" onClick={askEndAll}>
            {t('module.session.endAll')}
          </Button>
        )}
        <Button onClick={onClose}>{t('ra.action.close')}</Button>
      </DialogActions>
      <Confirm
        isOpen={Boolean(pending)}
        loading={acting}
        title={t('module.session.sessions')}
        content={pending?.content ?? ''}
        onConfirm={confirmed}
        onClose={() => setPending(null)}
      />
    </>
  );
};

export default UserSessionsPopup;
